import React from 'react';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import { authProvider } from './authProvider';
import { LoginPage } from './LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { StandaloneDriverDashboard } from './pages/StandaloneDriverDashboard';
import { StandaloneDriverProfile } from './pages/StandaloneDriverProfile';
import { SetupWizardPage } from './pages/SetupWizardPage';
import { MagicLoginPage } from './pages/MagicLoginPage';
import { Dashboard } from './Dashboard';
import { CustomLayout } from './CustomLayout';
import i18nProvider from './i18nProvider';
import { customDataProvider } from './customDataProvider';
import { theme } from './theme';
import { BrowserRouter, Route, Navigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import './globalStyles.css';

// Icons
import PeopleIcon from '@mui/icons-material/People';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import ShieldIcon from '@mui/icons-material/Shield';

// Resource imports
import { UserList, UserShow, UserCreate, UserEdit } from './resources/users';
import {
  EquipmentList,
  EquipmentShow,
  EquipmentCreate,
  EquipmentEdit,
} from './resources/equipment';
import insurance from './resources/insurance';
import {
  DriverList,
  DriverShow,
  DriverCreate,
  DriverEdit,
  DriverWizard,
} from './resources/drivers';
import {
  DriverLicenseList,
  DriverLicenseCreate,
  DriverLicenseEdit,
} from './resources/driver-licenses';
import {
  DriverCertificationList,
  DriverCertificationCreate,
  DriverCertificationEdit,
} from './resources/driver-certifications';
import {
  DriverEducationList,
  DriverEducationCreate,
  DriverEducationEdit,
} from './resources/driver-education';
import {
  DriverEmploymentList,
  DriverEmploymentCreate,
  DriverEmploymentEdit,
} from './resources/driver-employment';
import {
  DriverVehicleList,
  DriverVehicleCreate,
  DriverVehicleEdit,
} from './resources/driver-vehicles';
import {
  DriverTimeLogList,
  DriverTimeLogCreate,
  DriverTimeLogEdit,
  DriverTimeLogShow,
} from './resources/driver-time-logs';
import { EquipmentMaintenanceList, EquipmentMaintenanceShow } from './resources/equipment-maintenance';
import { EquipmentFuelList } from './resources/equipment-fuel';
import { EquipmentInspectionList } from './resources/equipment-inspections';
import { EquipmentAssignmentList } from './resources/equipment-assignments';
import { EquipmentIncidentList } from './resources/equipment-incidents';
import { CompletedFormsList, CompletedFormsShow } from './resources/completed-forms';
import TenantFormSettings from './resources/settings/TenantFormSettings';
import {
  ServiceProviderList,
  ServiceProviderShow,
  ServiceProviderCreate,
  ServiceProviderEdit,
} from './resources/service-providers';
import { RemindersList } from './resources/reminders';
import { NotificationsList } from './resources/notifications';
import DriverProfileBuilder from './components/DriverProfileBuilder';
import CompletedFormView from './pages/CompletedFormView';
import { CompanyProfilePage } from './pages/CompanyProfilePage';
import { TenantList } from './resources/tenants';

// Services imports for actions menu
import { ServicesList } from './services/ServicesList';
import { Reports } from './services/Reports';
import { ResumeExport } from './services/ResumeExport';
import { LicenseStatusCheck } from './services/LicenseStatusCheck';
import { BackgroundChecks } from './services/BackgroundChecks';
import { PSPReports } from './services/PSPReports';
import { LanguageProficiencyTest } from './services/LanguageProficiencyTest';

export const App = () => (
  <BrowserRouter>
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Admin
        authProvider={authProvider}
        dataProvider={customDataProvider}
        i18nProvider={i18nProvider}
        loginPage={LoginPage}
        layout={CustomLayout}
        title="Driver Verified Admin"
        disableTelemetry
        requireAuth
        theme={theme}
        dashboard={Dashboard}
      >
        <CustomRoutes noLayout>
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/setup-wizard" element={<SetupWizardPage />} />
          <Route path="/magic-login" element={<MagicLoginPage />} />
          <Route path="/standalone-dashboard" element={<StandaloneDriverDashboard />} />
          <Route path="/drivers/profile" element={<StandaloneDriverProfile />} />
          <Route path="/drivers/wizard" element={<DriverWizard />} />
          <Route path="/drivers/wizard/driver/:driverId" element={<DriverWizard />} />
          <Route path="/drivers/profile-builder" element={<DriverProfileBuilder />} />
          <Route path="/drivers" element={<Navigate to="/users" replace />} />
          <Route path="/drivers/*" element={<Navigate to="/users" replace />} />
        </CustomRoutes>
        
        <CustomRoutes>
          <Route path="/services" element={<ServicesList />} />
          <Route path="/services/reports" element={<Reports />} />
          <Route path="/services/resume" element={<ResumeExport />} />
          <Route path="/services/license-check" element={<LicenseStatusCheck />} />
          <Route path="/services/background-checks" element={<BackgroundChecks />} />
          <Route path="/services/psp-reports" element={<PSPReports />} />
          <Route path="/services/language-test" element={<LanguageProficiencyTest />} />
          <Route path="/settings/form-emails" element={<TenantFormSettings />} />
          <Route path="/completed-forms/:id/show" element={<CompletedFormView />} />
          <Route path="/company/profile" element={<CompanyProfilePage />} />
          <Route path="/locations" element={<Navigate to="/company/profile" replace />} />
          <Route path="/locations/*" element={<Navigate to="/company/profile" replace />} />
          <Route path="/cvors" element={<Navigate to="/company/profile" replace />} />
          <Route path="/cvors/*" element={<Navigate to="/company/profile" replace />} />
          <Route path="/tenants" element={<TenantList />} />
        </CustomRoutes>

        {/* Main resources */}
        <Resource
          name="users"
          list={UserList}
          show={UserShow}
          create={UserCreate}
          edit={UserEdit}
          icon={PeopleIcon}
          options={{ label: 'Team Members' }}
        />
        
        <Resource
          name="equipment"
          list={EquipmentList}
          show={EquipmentShow}
          create={EquipmentCreate}
          edit={EquipmentEdit}
          icon={LocalShippingIcon}
        />

        <Resource
          name="insurance"
          {...insurance}
          icon={ShieldIcon}
          options={{ label: 'Insurance' }}
        />

        {/* Driver time logs - new feature */}
        <Resource
          name="driver-time-logs"
          list={DriverTimeLogList}
          edit={DriverTimeLogEdit}
          show={DriverTimeLogShow}
          icon={AccessTimeIcon}
        />

        {/* Temporarily hidden - notifications functionality needs fixes
        <Resource
          name="notifications"
          list={NotificationsList}
          icon={NotificationImportantIcon}
          options={{ label: 'Notifications' }}
        />
        */}

        <Resource
          name="completed-forms"
          list={CompletedFormsList}
          show={CompletedFormsShow}
          options={{ label: 'Completed Forms' }}
        />

        <Resource
          name="locations"
        />

        <Resource
          name="cvors"
        />

        {/* Driver sub-resources */}
        <Resource
          name="driver-licenses"
          list={DriverLicenseList}
          create={DriverLicenseCreate}
          edit={DriverLicenseEdit}
        />

        <Resource
          name="driver-certifications"
          list={DriverCertificationList}
          create={DriverCertificationCreate}
          edit={DriverCertificationEdit}
        />

        <Resource
          name="driver-education"
          list={DriverEducationList}
          create={DriverEducationCreate}
          edit={DriverEducationEdit}
        />

        <Resource
          name="driver-employment"
          list={DriverEmploymentList}
          create={DriverEmploymentCreate}
          edit={DriverEmploymentEdit}
        />

        <Resource
          name="driver-vehicles"
          list={DriverVehicleList}
          create={DriverVehicleCreate}
          edit={DriverVehicleEdit}
        />

        {/* Equipment sub-resources - List only for reports */}
        <Resource name="equipment-maintenance" list={EquipmentMaintenanceList} show={EquipmentMaintenanceShow} />
        <Resource name="equipment-fuel" list={EquipmentFuelList} />
        <Resource name="equipment-inspections" list={EquipmentInspectionList} />
        <Resource name="equipment-assignments" list={EquipmentAssignmentList} />
        <Resource name="equipment-incidents" list={EquipmentIncidentList} />
        
        {/* Service providers */}
        <Resource
          name="service-providers"
          list={ServiceProviderList}
          show={ServiceProviderShow}
          create={ServiceProviderCreate}
          edit={ServiceProviderEdit}
          icon={BusinessIcon}
        />
        
        {/* Reminders */}
        <Resource
          name="reminders"
          list={RemindersList}
          icon={NotificationsIcon}
        />
        
        {/* Forms - hidden resource for reference inputs */}
        <Resource name="forms" />
      </Admin>
    </LocalizationProvider>
  </BrowserRouter>
);